import React, { useState, useEffect, useContext } from 'react';
import { MessageSquare, X, ChevronLeft } from 'lucide-react';
import ChatAdminSelector from './ChatAdminSelector';
import ChatWindow from './ChatWindow';
import { chatApi } from '../../api/chatApi';
import { useAuth } from '../../hooks/useAuth';
import { useSocket } from '../../hooks/useSocket';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const { user } = useAuth();
  const { socket, onlineUsers } = useSocket();

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await chatApi.getChatAdmins();
      setAdmins(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAdmins();
    }
  }, [isOpen]);

  const filteredAdmins = admins.filter((a) => {
    const term = search.toLowerCase();
    return (
      a.name.toLowerCase().includes(term) ||
      (a.company && a.company.name.toLowerCase().includes(term))
    );
  });

  const totalUnread = admins.reduce((sum, a) => sum + (a.unreadCount || 0), 0);

  return (
    <>
      {/* Floating Chat Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 p-4 rounded-2xl bg-gradient-to-tr from-green-700 to-green-600 text-white shadow-2xl shadow-green-700/50 hover:scale-105 active:scale-95 transition-all duration-200 group flex items-center justify-center"
      >
        <MessageSquare className="w-6 h-6 group-hover:rotate-12 transition-transform" />
        {totalUnread > 0 && (
          <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-rose-500 text-[10px] font-bold text-white shadow-md animate-bounce">
            {totalUnread}
          </span>
        )}
      </button>

      {/* Floating Drawer Modal */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] sm:w-[540px] md:w-[700px] h-[580px] bg-white border border-slate-200 rounded-3xl shadow-[0_20px_60px_rgb(0,0,0,0.12)] overflow-hidden flex flex-col animate-fadeIn">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 bg-white border-b border-slate-100">
            <div className="flex items-center space-x-2">
              {selectedAdmin && (
                <button
                  onClick={() => setSelectedAdmin(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-900 sm:hidden mr-1"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div className="p-1.5 bg-green-50 rounded-lg">
                <MessageSquare className="w-4 h-4 text-green-700" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 font-display">SuperAdmin Chat Hub</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-xl text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Split Pane View */}
          <div className="flex-1 flex overflow-hidden">
            <div
              className={`w-full sm:w-64 flex-shrink-0 ${
                selectedAdmin ? 'hidden sm:block' : 'block'
              }`}
            >
              <ChatAdminSelector
                admins={filteredAdmins}
                selectedAdmin={selectedAdmin}
                onSelectAdmin={(admin) => setSelectedAdmin(admin)}
                loading={loading}
                search={search}
                onSearchChange={setSearch}
              />
            </div>

            <div className={`flex-1 ${!selectedAdmin ? 'hidden sm:flex' : 'flex'}`}>
              <ChatWindow
                selectedAdmin={selectedAdmin}
                currentUserId={user?._id}
                socket={socket}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
